import {
  BackNextSkip,
  clearValidation,
  isValidInvestmentMonthlyContribution,
  isValidPercent,
  required,
  ruleRunner,
  run,
  TextInput,
} from '@assetmark/common-elements';
import { currencyFormatter, roundOfNumber } from '@assetmark/utilities';
import React, { useEffect, useState } from 'react';
import { TGoal } from '../../models/goal';
import { TOtherIncomeSourceType } from '../../models/other-income-source-type.model';
import { convertDollarStringToNumber } from '../../utils/format.utils';
import OtherIncomeSource from '../other-income-source/other-income-source';
import styles from './retirement-income.module.css';

const retirementIncomeAmountCalculate = (
  goal: TGoal,
  monthlyIncomeToRetirePercent?: any,
): string => {
  monthlyIncomeToRetirePercent = monthlyIncomeToRetirePercent
    ? monthlyIncomeToRetirePercent
    : goal.MonthlyIncomeToRetirePercent;
  let retirementIncomeAmount = 0;
  if (goal.MonthlyIncome > 0 && monthlyIncomeToRetirePercent > 0) {
    retirementIncomeAmount =
      (goal.MonthlyIncome * monthlyIncomeToRetirePercent) / 100;
  }
  return retirementIncomeAmount > 0
    ? currencyFormatter(retirementIncomeAmount)
    : '';
};

const MonthlyIncomeToRetirePercentCalculate = (
  goal: TGoal,
  retirementIncomeAmount: string,
): number => {
  let monthlyIncomeToRetirePercent = 0;
  const retirementIncomeAmountToNumber = retirementIncomeAmount
    ? parseFloat(retirementIncomeAmount)
    : 0;
  if (goal.MonthlyIncome > 0 && retirementIncomeAmountToNumber > 0) {
    monthlyIncomeToRetirePercent =
      (retirementIncomeAmountToNumber * 100) / goal.MonthlyIncome;
  }
  return monthlyIncomeToRetirePercent;
};

const otherInvestmentMonthlyContributionCalculation = (goal: TGoal): number => {
  const monthlyIncomeToRetirePercent = goal.MonthlyIncomeToRetirePercent
    ? roundOfNumber((goal.MonthlyIncomeToRetirePercent), 0)
    : 0;
  let netRetirementMonthlyIncome = 0;
  if (goal.MonthlyIncome > 0 && monthlyIncomeToRetirePercent > 0) {
    const retirementIncomeAmount = (goal.MonthlyIncome * monthlyIncomeToRetirePercent) / 100;
    const otherIncomeOptions = goal.OtherRetirementIncomes ? goal.OtherRetirementIncomes.filter(o => o.IncomeSourceTypeId) : []; 
    const otherAmount =  (otherIncomeOptions
      ? otherIncomeOptions.reduce((acc, obj) => {
          return obj.MonthlyIncome && obj.MonthlyIncome === 0
            ? acc
            : acc + obj.MonthlyIncome;
        }, 0)
      : 0);
      
    netRetirementMonthlyIncome = retirementIncomeAmount -  otherAmount;
  }

  return netRetirementMonthlyIncome;
};

export interface TRetirementIncomeProps {
  otherIncomeSourceType?: TOtherIncomeSourceType[];
  goal: TGoal;
  onBackButtonClick?: () => void;
  onSkipGoal?: () => void;
  saveRetirementIncome?: (goal: TGoal) => void;
}

const RetirementIncome: React.FC<TRetirementIncomeProps> = (props) => {
  const [goal, setGoal] = useState<TGoal>(props.goal);
  const [addSourceBtnDisabled, setAddSourceBtnDisabled] = useState(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({
    MonthlyIncomeToRetirePercent: '',
    NetRetirementMonthlyIncome: '',
    retirementIncomeAmountNumber: '',
  });
  const [buttonClass, setButtonClass] = useState<string>('disabled');
  const [isOIMCValid, setIsOIMCValid] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [
    retirementIncomeAmountCurrency,
    setRetirementIncomeAmountCurrency,
  ] = useState<string>(retirementIncomeAmountCalculate(props.goal));
  const [
    retirementIncomeAmountNumber,
    setRetirementIncomeAmountNumber,
  ] = useState<number>(
    convertDollarStringToNumber(retirementIncomeAmountCalculate(goal)),
  );

  const [fieldValidations, setFieldValidations] = useState<{
    MonthlyIncomeToRetirePercent: (state: any) => void;
    retirementIncomeAmountNumber: (state: any) => void;
    NetRetirementMonthlyIncome: (state: any) => void;
  }>({
    MonthlyIncomeToRetirePercent: ruleRunner(
      'MonthlyIncomeToRetirePercent',
      'amount',
      [isValidPercent(10, 300)],
    ),
    retirementIncomeAmountNumber: ruleRunner(
      'retirementIncomeAmountNumber',
      'amount',
      [required],
    ),
    NetRetirementMonthlyIncome: ruleRunner(
      'NetRetirementMonthlyIncome',
      'NetRetirementMonthlyIncome',
      [isValidInvestmentMonthlyContribution()],
    ),
  });

  useEffect(() => {
    setAddSourceBtnDisabled(
      goal.OtherRetirementIncomes.length >= 10 ? true : false,
    );
  }, []);

  useEffect(() => {
    if (props.goal && props.goal.MonthlyIncomeToRetirePercent) {
      validateRetirementIncome('MonthlyIncomeToRetirePercent', true);
    }
  }, [props.goal]);

  useEffect(() => {
    setButtonClass(isComplete && isOIMCValid ? '' : 'disabled');
  }, [isComplete, isOIMCValid]);

  const handleOnChange = (event: any): void => {
    const field = event.target.name;
    let value = event.target.value;
    const updatedStateGoal = { ...goal };
    let retirementIncomeAmountCurrency = '';
    if (field === 'MonthlyIncomeToRetirePercent') {
      updatedStateGoal[field] = value >= 0 ? roundOfNumber(value, 0) : null;
      retirementIncomeAmountCurrency = retirementIncomeAmountCalculate(
        updatedStateGoal,
        value,
      );
    } else {
      value = value === '' ? 0 : convertDollarStringToNumber(value);
      retirementIncomeAmountCurrency = value
        ? currencyFormatter(value)
        : event.target.value;
      updatedStateGoal.MonthlyIncomeToRetirePercent = roundOfNumber(
        MonthlyIncomeToRetirePercentCalculate(updatedStateGoal, value),
        0,
      );
    }

    updatedStateGoal.NetRetirementMonthlyIncome = otherInvestmentMonthlyContributionCalculation(
      updatedStateGoal,
    );

    setGoal(updatedStateGoal);
    setRetirementIncomeAmountCurrency(retirementIncomeAmountCurrency);
    setRetirementIncomeAmountNumber(
      convertDollarStringToNumber(retirementIncomeAmountCurrency),
    );

    validateRetirementIncome(field, true);
  };

  const validateRetirementIncome = (field: string, onChange: boolean) => {
    const goalState = { ...goal };
    let currentvalidationErrors;
    currentvalidationErrors = onChange
      ? run(goalState, [
          ruleRunner('MonthlyIncomeToRetirePercent', '', [clearValidation]),
        ])
      : run(goalState, [fieldValidations.MonthlyIncomeToRetirePercent]);
    currentvalidationErrors = {
      ...currentvalidationErrors,
      ...(onChange
        ? run(goalState, [
            ruleRunner('retirementIncomeAmountNumber', '', [clearValidation]),
          ])
        : run(goalState, [fieldValidations.retirementIncomeAmountNumber])),
    };
    currentvalidationErrors = {
      ...currentvalidationErrors,
      ...(onChange
        ? run(goalState, [
            ruleRunner('NetRetirementMonthlyIncome', '', [clearValidation]),
          ])
        : run(goalState, [fieldValidations.NetRetirementMonthlyIncome])),
    };

    const updatedValidationErrors = {
      ...validationErrors,
      ...currentvalidationErrors,
    };
    const isOIMCValid = isOtherInvestmentMonthlyContributionValid(goalState);
    setValidationErrors(updatedValidationErrors);
    setIsComplete(isRetirementIncomeValid(goalState, updatedValidationErrors));
    setShowErrors(true);
    setIsOIMCValid(isOIMCValid);
    setValidationErrors(updatedValidationErrors);
  };

  const isOtherInvestmentMonthlyContributionValid = (goal: TGoal): boolean => {

    let errorOnPage = false;
    if (goal.OtherRetirementIncomes) {
      goal.OtherRetirementIncomes.map((item, i) => {
        if (
          item.MonthlyIncome &&
          item.MonthlyIncome === 0 &&
          item.IncomeSourceTypeId > 0
        ) {
          errorOnPage = true;
        } else if (
          !(item.MonthlyIncome >= 0 && item.MonthlyIncome <= 9999999) &&
          item.IncomeSourceTypeId > 0
        ) {
          errorOnPage = true;
        }
        return item;
      });
    }

    if (goal.NetRetirementMonthlyIncome <= 0) {
      errorOnPage = true;
    }
    return !errorOnPage;
  };

  const isRetirementIncomeValid = (
    goal: TGoal,
    validationErrors?: any,
  ): boolean => {
    let errorOnPage = false;
    let returnItem = false;
    if (
      validationErrors &&
      (validationErrors.MonthlyIncomeToRetirePercent &&
        validationErrors.MonthlyIncomeToRetirePercent.length > 0)
    ) {
      errorOnPage = true;
    }
    if (
      goal.MonthlyIncomeToRetirePercent &&
      goal.MonthlyIncomeToRetirePercent >= 10 &&
      goal.MonthlyIncomeToRetirePercent <= 300 &&
      errorOnPage === false
    ) {
      returnItem = true;
    }
    return returnItem;
  };

  const addOtherIncome = (event: any): void => {
    const updatedStateGoal = { ...goal };
    updatedStateGoal.OtherRetirementIncomes = [
      ...goal.OtherRetirementIncomes,
      { Id: 0, IncomeSourceTypeId: 0 },
    ];
    const isOIMCValid = isOtherInvestmentMonthlyContributionValid(
      updatedStateGoal,
    );

    setGoal(updatedStateGoal);
    setIsOIMCValid(isOIMCValid);
    setButtonClass(isOIMCValid ? '' : 'disabled');
    setAddSourceBtnDisabled(
      updatedStateGoal.OtherRetirementIncomes.length >= 10 ? true : false,
    );
  };

  const removeOtherIncome = (index: number): void => {
    const updatedStateGoal = { ...goal };
    updatedStateGoal.OtherRetirementIncomes = [
      ...goal.OtherRetirementIncomes.filter((item, i) => i !== index),
    ];
    updatedStateGoal.NetRetirementMonthlyIncome = otherInvestmentMonthlyContributionCalculation(
      updatedStateGoal,
    );
    
    const isOIMCValid = isOtherInvestmentMonthlyContributionValid(
      updatedStateGoal,
    );

    setGoal(updatedStateGoal);
    setIsOIMCValid(isOIMCValid);
    setButtonClass(isOIMCValid ? '' : 'disabled');
    setAddSourceBtnDisabled(
      updatedStateGoal.OtherRetirementIncomes.length >= 10 ? true : false,
    );
  };

  const onChangeOtherIncomeSource = (
    event: any,
    otherIncomeItem: any,
    index: number,
  ): void => {
    const field = event.target.name;

    const value = event.target.value;
    const updatedStateGoal = { ...goal };
    // check other than default drop down value
    if (field === 'monthlyIncome' && otherIncomeItem.IncomeSourceTypeId > 0) {
      // update the corresponding field state
      updatedStateGoal.OtherRetirementIncomes = [
        ...goal.OtherRetirementIncomes.map((item, i) => {
          if (i === index) {
            const formattedvalue = convertDollarStringToNumber(value);
            const tempItem = { ...item, MonthlyIncome: formattedvalue };
            return tempItem;
          } else {
            return item;
          }
        }),
      ];
    } else if (field === 'otherIncomeSourceType') {
      // update the corresponding drop down value
      updatedStateGoal.OtherRetirementIncomes = [
        ...goal.OtherRetirementIncomes.map((item, i) => {
          if (i === index) {
            const tempItem = {
              ...item,
              IncomeSourceTypeId: parseInt(value),
              MonthlyIncome: parseInt(value) === 0 ? 0 : 0,
            };
            return tempItem;
          } else {
            return item;
          }
        }),
      ];
    }

    // calculate NetRetirementMonthlyIncome on every change
    updatedStateGoal.NetRetirementMonthlyIncome = otherInvestmentMonthlyContributionCalculation(
      updatedStateGoal,
    );
    
    setGoal(updatedStateGoal);
    validateOtherInvestmentMonthlyContribution(field, true, updatedStateGoal);
  };

  const validateOtherInvestmentMonthlyContribution = (
    field: string,
    onChange: boolean,
    goal: TGoal,
  ) => {
    const goalState = { ...goal };
    let errors;
    errors = run(goalState, [fieldValidations.NetRetirementMonthlyIncome]);
    const updatedValidationErrors = { ...validationErrors, ...errors };

    const isOIMCValid = isOtherInvestmentMonthlyContributionValid(goal);

    setGoal(goalState);
    setIsOIMCValid(isOIMCValid);
    setShowErrors(true);
    setValidationErrors(updatedValidationErrors);

    setButtonClass(isOIMCValid ? '' : 'disabled');
  };

  const handleBackButtonClick = (event: any): void => {
    props.onBackButtonClick && props.onBackButtonClick();
  };
  const handleRetirementIncomeSave = (event: any): void => {
    props.saveRetirementIncome && props.saveRetirementIncome(goal);
  };
  console.log(`<< :TGoal: >>`, goal);
  return (
    <div className={styles.retirement_income_component}>
      <div className="col-sm-12">
        <h1 className={styles.title}>Desired Retirement Income</h1>
        <div className="row">
          <div className={`col-sm-12 ${styles.label_text}`}>
            I’d like to have
            <TextInput
              className={`${styles.input_text} ${styles.percent_text} ${
                validationErrors.MonthlyIncomeToRetirePercent &&
                validationErrors.MonthlyIncomeToRetirePercent.length
                  ? styles.errorClass
                  : ''
              }`}
              value={
                goal.MonthlyIncomeToRetirePercent !== 0
                  ? roundOfNumber(goal.MonthlyIncomeToRetirePercent, 0)
                  : ''
              }
              name="MonthlyIncomeToRetirePercent"
              type="underline"
              maxLength={3}
              showError={showErrors}
              errorText={validationErrors.MonthlyIncomeToRetirePercent || ''}
              onChange={handleOnChange}
              onBlur={() =>
                validateRetirementIncome('MonthlyIncomeToRetirePercent', false)
              }
            />
            % of my current monthly income or
          </div>
        </div>
        <div className="row mt-30">
          <div className={`col-sm-12 ${styles.label_text} ${styles.mt_row}`}>
            $
            <TextInput
              className={`${styles.input_text} ${styles.amount_text} ${
                styles.percent_text
              } ${
                validationErrors.retirementIncomeAmountNumber &&
                validationErrors.retirementIncomeAmountNumber.length
                  ? styles.errorClass
                  : ''
              }`}
              value={retirementIncomeAmountCurrency}
              name="retirementIncomeAmountNumber"
              type="underline"
              maxLength={10}
              showError={showErrors}
              errorText={validationErrors.retirementIncomeAmountNumber || ''}
              onChange={handleOnChange}
              onBlur={() =>
                validateRetirementIncome('retirementIncomeAmountNumber', false)
              }
            />
            per month in retirement.
          </div>
        </div>
        <div className="row mt-30">
          <div className={`col-sm-12 ${styles.label_text} ${styles.mt_row}`}>
            I have monthly retirement income from these other sources:
            <OtherIncomeSource
              name={'otherIncomeSource'}
              addOtherIncome={addOtherIncome}
              otherIncomeSource={goal.OtherRetirementIncomes}
              options={props.otherIncomeSourceType}
              removeOtherIncome={removeOtherIncome}
              onChange={onChangeOtherIncomeSource}
              addSourceBtnDisabled={addSourceBtnDisabled}
            />
          </div>
        </div>
        <div className="row mt-30">
          <div
            className={`col-sm-12 ${styles.label_text} ${styles.subTitle_text} ${styles.mt_row}`}
          >
            To reach my total retirement income goal, I’d like this
          </div>
        </div>
        <div className={`row ${styles.mt20}`}>
          <div className={`col-sm-12 ${styles.label_text}`}>
            investment to provide
          </div>
        </div>
        <div className={`row ${styles.mt30}`}>
          <div className={`col-sm-12 ${styles.label_text}`}>
            $
            <TextInput
              className={`${styles.input_text} ${styles.amount_text}  ${
                validationErrors.NetRetirementMonthlyIncome &&
                validationErrors.NetRetirementMonthlyIncome.length
                  ? styles.errorClass
                  : ''
              }`}
              value={
                goal.NetRetirementMonthlyIncome !== 0
                  ? currencyFormatter(goal.NetRetirementMonthlyIncome)
                  : ''
              }
              name="NetRetirementMonthlyIncome"
              type="underline"
              maxLength={10}
              showError={showErrors}
              errorText={validationErrors.NetRetirementMonthlyIncome || ''}
            />
            per month in retirement.
          </div>
        </div>
        <div className={styles.mt_row_btn}>
          <BackNextSkip
            backButtonClassName={`button button-secondary button-lg ${styles.btnLineHeight}`}
            backButtonText="BACK"
            onBackButtonClickHandler={handleBackButtonClick}
            nextButtonClassName={`button button-primary button-lg ml-2 ${styles.btnLineHeight}`}
            nextButtonText="NEXT"
            onNextButtonClickHandler={handleRetirementIncomeSave}
            isDisabledNextButton={isComplete && isOIMCValid ? false : true}
          />
        </div>
      </div>
    </div>
  );
};

export default RetirementIncome;
